import { useState, useEffect, useCallback } from "react";

const API      = process.env.REACT_APP_API_URL || "http://localhost:4000/api";
const getToken = () => localStorage.getItem("sellsync_token");

const api = async (path, opts = {}) => {
  const res = await fetch(API + path, {
    ...opts,
    headers: { "Content-Type":"application/json", ...(getToken()?{Authorization:"Bearer "+getToken()}:{}), ...opts.headers },
  });
  if (opts.raw) return res;
  const data = await res.json();
  if (!res.ok) throw new Error(data.error || "Request failed");
  return data;
};

const INR = (n) => "₹" + Number(n||0).toLocaleString("en-IN");

const S = {
  card:    { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", marginBottom:14 },
  cardHdr: { padding:"11px 16px", borderBottom:"1px solid #e5e7eb", display:"flex", alignItems:"center", justifyContent:"space-between", fontWeight:600, fontSize:13 },
  btn:     (c="#F47920") => ({ padding:"8px 16px", borderRadius:7, fontSize:12, cursor:"pointer", background:c, color:"#fff", border:"none", fontFamily:"inherit", fontWeight:600 }),
  btnOut:  { padding:"7px 14px", borderRadius:7, fontSize:12, cursor:"pointer", background:"#fff", color:"#374151", border:"1px solid #d1d5db", fontFamily:"inherit" },
  input:   { width:"100%", padding:"7px 10px", borderRadius:7, border:"1px solid #d1d5db", fontSize:13, fontFamily:"inherit", outline:"none", marginBottom:10 },
  label:   { fontSize:11, color:"#6b7280", fontWeight:600, display:"block", marginBottom:3 },
  tab:     (a) => ({ padding:"10px 20px", background:"none", border:"none", borderBottom:a?"2px solid #F47920":"2px solid transparent", color:a?"#F47920":"#6b7280", fontWeight:a?600:400, fontSize:13, cursor:"pointer", fontFamily:"inherit" }),
  kpi:     { background:"#fff", borderRadius:10, border:"1px solid #e5e7eb", padding:"14px 16px" },
  th:      { padding:"8px 12px", textAlign:"left", fontSize:11, fontWeight:600, color:"#6b7280", background:"#f9fafb" },
  td:      { padding:"9px 12px", borderBottom:"1px solid #f3f4f6", fontSize:13, verticalAlign:"middle" },
};

const Badge = ({ label, color, bg }) => (
  <span style={{ background:bg, color, padding:"2px 9px", borderRadius:20, fontSize:11, fontWeight:600 }}>{label}</span>
);

export default function PageAccounting({ toast, invoices=[], orders=[], tenant }) {
  const [tab,        setTab]        = useState("tally");
  const [dateFrom,   setDateFrom]   = useState(() => { const d = new Date(); d.setDate(1); return d.toISOString().split("T")[0]; });
  const [dateTo,     setDateTo]     = useState(new Date().toISOString().split("T")[0]);
  const [dataType,   setDataType]   = useState("sales");
  const [exporting,  setExporting]  = useState("");
  const [zohoStatus, setZohoStatus] = useState(null);
  const [syncing,    setSyncing]    = useState("");
  const [syncResults,setSyncResults]= useState(null);
  const [companyName,setCompanyName]= useState(tenant?.storeName || "");

  // filter data by date range
  const filteredInvoices = invoices.filter((inv) => {
    const d = new Date(inv.date || inv.createdAt || Date.now());
    return d >= new Date(dateFrom) && d <= new Date(dateTo + "T23:59:59");
  });

  const filteredPOs = orders.filter ? [] : []; // POs passed separately

  // ── TALLY XML DOWNLOAD ────────────────────────────────────────
  const downloadTallyXML = async (type) => {
    setExporting("tally_" + type);
    try {
      const res = await fetch(API + "/accounting/tally/" + type, {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken() },
        body: JSON.stringify({
          invoices:    type === "sales"     ? filteredInvoices : undefined,
          orders:      type === "purchases" ? orders           : undefined,
          companyName,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = "tally_" + type + "_" + dateFrom + ".xml";
      a.click();
      toast("Tally XML downloaded ✓", "success");
    } catch(e) { toast(e.message, "error"); }
    finally { setExporting(""); }
  };

  // ── CSV DOWNLOAD ──────────────────────────────────────────────
  const downloadCSV = async (type) => {
    setExporting("csv_" + type);
    try {
      const res = await fetch(API + "/accounting/export/csv", {
        method:  "POST",
        headers: { "Content-Type":"application/json", Authorization:"Bearer "+getToken() },
        body: JSON.stringify({
          type,
          data: type === "sales" ? filteredInvoices : orders,
        }),
      });
      if (!res.ok) { const d = await res.json(); throw new Error(d.error); }
      const blob = await res.blob();
      const url  = URL.createObjectURL(blob);
      const a    = document.createElement("a");
      a.href     = url;
      a.download = type + "_export_" + dateFrom + ".csv";
      a.click();
      toast("CSV exported ✓", "success");
    } catch(e) { toast(e.message, "error"); }
    finally { setExporting(""); }
  };

  // ── ZOHO SYNC ─────────────────────────────────────────────────
  const testZoho = async () => {
    try {
      const data = await api("/accounting/zoho/test");
      setZohoStatus({ ok:true, message:data.message, org:data.org });
      toast("Zoho connected ✓", "success");
    } catch(e) {
      setZohoStatus({ ok:false, message:e.message });
      toast(e.message, "error");
    }
  };

  const syncToZoho = async (type) => {
    setSyncing(type);
    setSyncResults(null);
    try {
      const data = await api("/accounting/zoho/" + (type==="sales"?"invoices":"bills"), {
        method: "POST",
        body: JSON.stringify({
          invoices: type === "sales"     ? filteredInvoices : undefined,
          orders:   type === "purchases" ? orders           : undefined,
        }),
      });
      setSyncResults(data);
      toast("Synced " + data.synced + " records to Zoho ✓", "success");
    } catch(e) { toast(e.message, "error"); }
    finally { setSyncing(""); }
  };

  // ── summary stats ─────────────────────────────────────────────
  const totalSales     = filteredInvoices.reduce((a,inv) => a+(inv.total||0), 0);
  const totalTaxable   = Math.round(totalSales / 1.18);
  const totalGST       = totalSales - totalTaxable;

  return (
    <div>
      {/* KPIs */}
      <div style={{ display:"grid", gridTemplateColumns:"repeat(4,1fr)", gap:12, marginBottom:14 }}>
        {[
          { label:"Total Sales",      value:INR(totalSales),       color:"#F47920" },
          { label:"Taxable Amount",   value:INR(totalTaxable),     color:"#374151" },
          { label:"GST Collected",    value:INR(Math.round(totalGST)), color:"#2563eb" },
          { label:"Invoices",         value:filteredInvoices.length,   color:"#7c3aed" },
        ].map((k) => (
          <div key={k.label} style={S.kpi}>
            <div style={{ fontSize:11, color:"#6b7280", marginBottom:6 }}>{k.label}</div>
            <div style={{ fontSize:22, fontWeight:700, color:k.color }}>{k.value}</div>
          </div>
        ))}
      </div>

      {/* date filter */}
      <div style={{ background:"#fff", border:"1px solid #e5e7eb", borderRadius:10, padding:"12px 16px", marginBottom:14, display:"flex", gap:12, alignItems:"center", flexWrap:"wrap" }}>
        <span style={{ fontSize:13, fontWeight:600 }}>Period:</span>
        <div>
          <label style={{ ...S.label, display:"inline", marginRight:6 }}>From</label>
          <input type="date" style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
            value={dateFrom} onChange={(e) => setDateFrom(e.target.value)}/>
        </div>
        <div>
          <label style={{ ...S.label, display:"inline", marginRight:6 }}>To</label>
          <input type="date" style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit" }}
            value={dateTo} onChange={(e) => setDateTo(e.target.value)}/>
        </div>
        <span style={{ fontSize:12, color:"#9ca3af" }}>{filteredInvoices.length} invoices in range</span>
        <div style={{ flex:1 }}/>
        <div>
          <label style={{ ...S.label, display:"inline", marginRight:6 }}>Company Name</label>
          <input style={{ padding:"5px 8px", borderRadius:6, border:"1px solid #d1d5db", fontSize:12, fontFamily:"inherit", width:200 }}
            value={companyName} onChange={(e) => setCompanyName(e.target.value)}/>
        </div>
      </div>

      {/* tabs */}
      <div style={{ display:"flex", borderBottom:"1px solid #e5e7eb", marginBottom:16 }}>
        {[
          { id:"tally",  label:"🧮 Tally Export"    },
          { id:"zoho",   label:"📚 Zoho Books Sync"  },
          { id:"preview",label:"👁 Data Preview"     },
        ].map((t) => (
          <button key={t.id} style={S.tab(tab===t.id)} onClick={() => setTab(t.id)}>{t.label}</button>
        ))}
      </div>

      {/* ══ TALLY TAB ══ */}
      {tab === "tally" && (
        <>
          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {/* Sales XML */}
            <div style={S.card}>
              <div style={S.cardHdr}>
                <div>
                  <div>Sales Vouchers → Tally</div>
                  <div style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>Imports as Sales vouchers in Tally Prime</div>
                </div>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ background:"#f0fdf4", borderRadius:8, padding:12, marginBottom:14, fontSize:12, color:"#15803d" }}>
                  ✓ {filteredInvoices.length} invoices ready to export<br/>
                  ✓ Total: {INR(totalSales)} | GST: {INR(Math.round(totalGST))}
                </div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:14, lineHeight:1.6 }}>
                  <strong>How to import in Tally Prime:</strong><br/>
                  1. Download the XML file<br/>
                  2. Open Tally Prime → Gateway of Tally<br/>
                  3. Go to Import → Vouchers<br/>
                  4. Select the downloaded XML file<br/>
                  5. Click Import
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button style={{ ...S.btn(), flex:1, opacity:exporting==="tally_sales"?0.7:1 }}
                    onClick={() => downloadTallyXML("sales")} disabled={!!exporting}>
                    {exporting==="tally_sales" ? "Generating…" : "⬇ Download Tally XML"}
                  </button>
                  <button style={{ ...S.btnOut, opacity:exporting==="csv_sales"?0.7:1 }}
                    onClick={() => downloadCSV("sales")} disabled={!!exporting}>
                    {exporting==="csv_sales" ? "…" : "CSV"}
                  </button>
                </div>
              </div>
            </div>

            {/* Purchase XML */}
            <div style={S.card}>
              <div style={S.cardHdr}>
                <div>
                  <div>Purchase Vouchers → Tally</div>
                  <div style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>Imports as Purchase vouchers in Tally Prime</div>
                </div>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ background:"#eff6ff", borderRadius:8, padding:12, marginBottom:14, fontSize:12, color:"#2563eb" }}>
                  ✓ {orders.length} purchase orders ready to export
                </div>
                <div style={{ fontSize:12, color:"#6b7280", marginBottom:14, lineHeight:1.6 }}>
                  <strong>How to import in Tally Prime:</strong><br/>
                  1. Download the XML file<br/>
                  2. Open Tally Prime → Gateway of Tally<br/>
                  3. Go to Import → Vouchers<br/>
                  4. Select the downloaded XML file<br/>
                  5. Click Import
                </div>
                <div style={{ display:"flex", gap:8 }}>
                  <button style={{ ...S.btn("#2563eb"), flex:1, opacity:exporting==="tally_purchases"?0.7:1 }}
                    onClick={() => downloadTallyXML("purchases")} disabled={!!exporting}>
                    {exporting==="tally_purchases" ? "Generating…" : "⬇ Download Tally XML"}
                  </button>
                  <button style={{ ...S.btnOut, opacity:exporting==="csv_purchases"?0.7:1 }}
                    onClick={() => downloadCSV("purchases")} disabled={!!exporting}>
                    {exporting==="csv_purchases" ? "…" : "CSV"}
                  </button>
                </div>
              </div>
            </div>
          </div>

          {/* Tally ledger setup guide */}
          <div style={{ ...S.card, padding:16 }}>
            <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>📋 Required Ledgers in Tally Prime</div>
            <div style={{ fontSize:12, color:"#6b7280", marginBottom:10 }}>Create these ledgers in Tally before importing:</div>
            <div style={{ display:"grid", gridTemplateColumns:"repeat(3,1fr)", gap:8 }}>
              {[
                { ledger:"Sales",        group:"Sales Accounts",   type:"sales" },
                { ledger:"Purchase",     group:"Purchase Accounts",type:"purchase" },
                { ledger:"Output CGST",  group:"Duties & Taxes",   type:"tax" },
                { ledger:"Output SGST",  group:"Duties & Taxes",   type:"tax" },
                { ledger:"Input CGST",   group:"Duties & Taxes",   type:"tax" },
                { ledger:"Input SGST",   group:"Duties & Taxes",   type:"tax" },
              ].map((l) => (
                <div key={l.ledger} style={{ background:"#f9fafb", borderRadius:7, padding:"8px 10px", border:"1px solid #e5e7eb" }}>
                  <div style={{ fontWeight:600, fontSize:12 }}>{l.ledger}</div>
                  <div style={{ fontSize:10, color:"#9ca3af" }}>Group: {l.group}</div>
                </div>
              ))}
            </div>
          </div>
        </>
      )}

      {/* ══ ZOHO TAB ══ */}
      {tab === "zoho" && (
        <>
          {/* connection status */}
          <div style={{ ...S.card, padding:16, marginBottom:14 }}>
            <div style={{ display:"flex", justifyContent:"space-between", alignItems:"center" }}>
              <div>
                <div style={{ fontWeight:600, fontSize:14, marginBottom:4 }}>Zoho Books Connection</div>
                {zohoStatus ? (
                  <div style={{ fontSize:13, color:zohoStatus.ok?"#15803d":"#dc2626" }}>
                    {zohoStatus.ok ? "✓ " + zohoStatus.message + (zohoStatus.org?" — "+zohoStatus.org:"") : "✗ " + zohoStatus.message}
                  </div>
                ) : (
                  <div style={{ fontSize:12, color:"#9ca3af" }}>
                    Connect Zoho Books in Settings → Zoho Books tab to enable direct sync
                  </div>
                )}
              </div>
              <button style={S.btn()} onClick={testZoho}>Test Connection</button>
            </div>
          </div>

          <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:14 }}>
            {/* sync invoices */}
            <div style={S.card}>
              <div style={S.cardHdr}>
                <div>
                  <div>Sync Sales Invoices → Zoho Books</div>
                  <div style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>Creates invoices directly in Zoho Books</div>
                </div>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ background:"#f0fdf4", borderRadius:8, padding:12, marginBottom:14, fontSize:12, color:"#15803d" }}>
                  {filteredInvoices.length} invoices will be synced to Zoho Books
                </div>
                {syncResults && syncing === "" && (
                  <div style={{ background:"#eff6ff", borderRadius:8, padding:12, marginBottom:14, fontSize:12 }}>
                    <div style={{ fontWeight:600, color:"#2563eb", marginBottom:4 }}>Last sync result</div>
                    <div>✓ {syncResults.synced} synced | ✗ {syncResults.failed} failed</div>
                  </div>
                )}
                <button style={{ ...S.btn(), width:"100%", opacity:syncing==="sales"?0.7:1 }}
                  onClick={() => syncToZoho("sales")} disabled={!!syncing}>
                  {syncing==="sales" ? "Syncing…" : "↑ Sync " + filteredInvoices.length + " Invoices to Zoho"}
                </button>
              </div>
            </div>

            {/* sync bills */}
            <div style={S.card}>
              <div style={S.cardHdr}>
                <div>
                  <div>Sync Purchase Orders → Zoho Books</div>
                  <div style={{ fontSize:11, color:"#9ca3af", fontWeight:400 }}>Creates bills directly in Zoho Books</div>
                </div>
              </div>
              <div style={{ padding:16 }}>
                <div style={{ background:"#eff6ff", borderRadius:8, padding:12, marginBottom:14, fontSize:12, color:"#2563eb" }}>
                  {orders.length} purchase orders will be synced as bills
                </div>
                <button style={{ ...S.btn("#2563eb"), width:"100%", opacity:syncing==="purchases"?0.7:1 }}
                  onClick={() => syncToZoho("purchases")} disabled={!!syncing}>
                  {syncing==="purchases" ? "Syncing…" : "↑ Sync " + orders.length + " POs to Zoho"}
                </button>
              </div>
            </div>
          </div>

          {/* Zoho setup guide */}
          <div style={{ ...S.card, padding:16 }}>
            <div style={{ fontWeight:600, fontSize:13, marginBottom:12 }}>🔧 How to connect Zoho Books</div>
            <div style={{ display:"grid", gridTemplateColumns:"1fr 1fr", gap:16 }}>
              <div>
                <div style={{ fontWeight:600, fontSize:12, marginBottom:8, color:"#374151" }}>Step 1 — Create Zoho API app</div>
                {[
                  "Go to api-console.zoho.in",
                  "Create new Server-based application",
                  "Add scope: ZohoBooks.fullaccess.all",
                  "Copy Client ID and Client Secret",
                ].map((s,i) => <div key={i} style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>{i+1}. {s}</div>)}
              </div>
              <div>
                <div style={{ fontWeight:600, fontSize:12, marginBottom:8, color:"#374151" }}>Step 2 — Add to 3June Settings</div>
                {[
                  "Go to Settings → Connect Platforms",
                  "Select 'Zoho Books'",
                  "Enter Client ID, Client Secret",
                  "Enter your Org ID and Refresh Token",
                  "Click Connect",
                ].map((s,i) => <div key={i} style={{ fontSize:12, color:"#6b7280", marginBottom:4 }}>{i+1}. {s}</div>)}
              </div>
            </div>
          </div>
        </>
      )}

      {/* ══ PREVIEW TAB ══ */}
      {tab === "preview" && (
        <>
          <div style={{ display:"flex", gap:10, marginBottom:14 }}>
            <button style={{ ...S.btn(dataType==="sales"?"#F47920":"#9ca3af") }} onClick={() => setDataType("sales")}>Sales ({filteredInvoices.length})</button>
            <button style={{ ...S.btn(dataType==="purchases"?"#2563eb":"#9ca3af") }} onClick={() => setDataType("purchases")}>Purchases ({orders.length})</button>
          </div>

          {dataType === "sales" && (
            <div style={S.card}>
              <div style={S.cardHdr}><span>Sales Invoices Preview</span><span style={{ fontSize:12, color:"#9ca3af" }}>{dateFrom} to {dateTo}</span></div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    {["Invoice No","Date","Customer","Platform","Amount","Taxable","CGST","SGST","Total"].map((h) => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {filteredInvoices.length === 0 ? (
                      <tr><td colSpan={9} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>
                        No invoices in selected period
                      </td></tr>
                    ) : filteredInvoices.map((inv, i) => {
                      const total   = inv.total || 0;
                      const taxable = Math.round(total / 1.18);
                      const gst     = total - taxable;
                      return (
                        <tr key={i} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                          <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#F47920" }}>{inv.id || inv.invoiceNumber}</td>
                          <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{new Date(inv.date||inv.createdAt||Date.now()).toLocaleDateString("en-IN")}</td>
                          <td style={{ ...S.td, fontWeight:500 }}>{inv.customer || inv.customerName}</td>
                          <td style={S.td}><Badge label={inv.platform||"manual"} color="#374151" bg="#f3f4f6"/></td>
                          <td style={{ ...S.td, fontWeight:600 }}>{INR(total)}</td>
                          <td style={S.td}>{INR(taxable)}</td>
                          <td style={{ ...S.td, color:"#2563eb" }}>{INR(Math.round(gst/2))}</td>
                          <td style={{ ...S.td, color:"#7c3aed" }}>{INR(Math.round(gst/2))}</td>
                          <td style={{ ...S.td, fontWeight:700, color:"#F47920" }}>{INR(total)}</td>
                        </tr>
                      );
                    })}
                    {filteredInvoices.length > 0 && (
                      <tr style={{ background:"#f9fafb", fontWeight:700 }}>
                        <td style={S.td} colSpan={5}>Total ({filteredInvoices.length} invoices)</td>
                        <td style={S.td}>{INR(totalTaxable)}</td>
                        <td style={{ ...S.td, color:"#2563eb" }}>{INR(Math.round(totalGST/2))}</td>
                        <td style={{ ...S.td, color:"#7c3aed" }}>{INR(Math.round(totalGST/2))}</td>
                        <td style={{ ...S.td, color:"#F47920" }}>{INR(totalSales)}</td>
                      </tr>
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {dataType === "purchases" && (
            <div style={S.card}>
              <div style={S.cardHdr}><span>Purchase Orders Preview</span></div>
              <div style={{ overflowX:"auto" }}>
                <table style={{ width:"100%", borderCollapse:"collapse" }}>
                  <thead><tr>
                    {["PO Number","Date","Supplier","Items","Subtotal","GST","Total","Status"].map((h) => (
                      <th key={h} style={S.th}>{h}</th>
                    ))}
                  </tr></thead>
                  <tbody>
                    {orders.length === 0 ? (
                      <tr><td colSpan={8} style={{ ...S.td, textAlign:"center", color:"#9ca3af", padding:40 }}>No purchase orders yet</td></tr>
                    ) : orders.map((po, i) => (
                      <tr key={i} onMouseEnter={(e) => e.currentTarget.style.background="#fafafa"} onMouseLeave={(e) => e.currentTarget.style.background=""}>
                        <td style={{ ...S.td, fontFamily:"monospace", fontSize:11, color:"#2563eb" }}>{po.poNumber}</td>
                        <td style={{ ...S.td, fontSize:11, color:"#6b7280" }}>{new Date(po.createdAt||Date.now()).toLocaleDateString("en-IN")}</td>
                        <td style={{ ...S.td, fontWeight:500 }}>{po.supplierName}</td>
                        <td style={S.td}>{po.items?.length || 0}</td>
                        <td style={S.td}>{INR(po.subtotal)}</td>
                        <td style={{ ...S.td, color:"#2563eb" }}>{INR(po.gstAmount)}</td>
                        <td style={{ ...S.td, fontWeight:700 }}>{INR(po.totalAmount)}</td>
                        <td style={S.td}><Badge label={po.status} color="#15803d" bg="#f0fdf4"/></td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </>
      )}
    </div>
  );
}
